/**
 * SEOHead Component - Comprehensive SEO Management for Lotterics
 * 
 * Features:
 * - Self-referencing canonical URLs (always https://lotterics.com/{lang}/...)
 * - Unique metadata per page with localized descriptions
 * - OpenGraph & Twitter cards
 * - JSON-LD structured data (WebSite, Organization, BreadcrumbList)
 * - hreflang tags for multilingual support (EN, IT, FR, ES, DE)
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocation } from 'react-router-dom';

// Fixed production domain for canonical URLs - NEVER use window.location
const CANONICAL_BASE = 'https://lotterics.com';
const SITE_NAME = 'Lotterics';

// All supported languages for hreflang
const SUPPORTED_LANGUAGES = ['en', 'it', 'fr', 'es', 'de'];

/**
 * Generate breadcrumb items from path
 */
function generateBreadcrumbs(path, lang = 'en') {
  const segments = path.split('/').filter(Boolean);
  // Root canonical is lotterics.com
  const homeUrl = lang === 'en' ? CANONICAL_BASE : `${CANONICAL_BASE}/${lang}`;
  const breadcrumbs = [
    { name: 'Home', url: homeUrl }
  ];
  
  let currentPath = lang === 'en' ? '' : `/${lang}`;
  
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    if (segment === lang) continue; // Skip language segment
    
    currentPath += `/${segment}`;
    
    // Format segment name
    let name = segment
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
    
    // Special cases
    if (segment === 'lottery') name = 'Lotteries';
    if (segment === 'euromillions') name = 'EuroMillions';
    if (segment === 'megamillions') name = 'Mega Millions';
    if (segment === 'powerball') name = 'Powerball';
    if (segment === 'superenalotto') name = 'SuperEnalotto';
    if (segment === 'laprimitiva') name = 'La Primitiva';
    if (segment === 'swisslotto') name = 'Swiss Lotto';
    if (segment === 'eurodreams') name = 'EuroDreams';
    if (segment === 'megasena') name = 'Mega-Sena';
    
    breadcrumbs.push({
      name,
      url: `${CANONICAL_BASE}${currentPath}`
    });
  }
  
  return breadcrumbs;
}

/**
 * Generate JSON-LD BreadcrumbList schema
 */
function generateBreadcrumbSchema(breadcrumbs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': item.url
    }))
  };
}

/**
 * Generate WebSite + Organization schema
 */
function generateWebsiteSchema(lang = 'en', siteDescription) {
  // Root canonical is lotterics.com (no /en for English)
  const baseUrl = lang === 'en' ? CANONICAL_BASE : `${CANONICAL_BASE}/${lang}`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${CANONICAL_BASE}/#website`,
        'url': CANONICAL_BASE,
        'name': SITE_NAME,
        'description': siteDescription,
        'inLanguage': lang,
        'publisher': { '@id': `${CANONICAL_BASE}/#organization` }
      },
      {
        '@type': 'Organization',
        '@id': `${CANONICAL_BASE}/#organization`,
        'name': SITE_NAME,
        'url': CANONICAL_BASE,
        'description': siteDescription
      }
    ]
  };
}

export const SEOHead = ({ 
  title, 
  description, 
  path = '', 
  type = 'website',
  image = '/og-image.jpg',
  article = null,
  noindex = false,
  lottery = null  // For lottery-specific pages
}) => {
  const { currentLang, t } = useLanguage();
  const location = useLocation();
  
  // Get localized site description from i18n
  const localizedSiteDescription = t('seo.site_description');
  
  // Build the full title
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — ${t('seo.home_title')}`;
  
  // Use provided description, or fallback to localized site description
  const pageDescription = description || localizedSiteDescription;
  
  // Build canonical URL - ALWAYS use fixed domain
  // English homepage: https://lotterics.com/
  // Other language homepages: https://lotterics.com/{lang}
  // English inner pages: https://lotterics.com/{path}
  // Other language inner pages: https://lotterics.com/{lang}/{path}
  const cleanPath = path.startsWith('/') ? path : (path ? `/${path}` : '');
  
  // Determine the language-free path for canonical URL generation
  // Strip any language prefix from BOTH the provided path and the location pathname
  const langPrefixRegex = new RegExp(`^/(${SUPPORTED_LANGUAGES.join('|')})(/|$)`);
  const rawPath = cleanPath || location.pathname || '';
  const actualPath = rawPath.replace(langPrefixRegex, '/').replace(/^\/$/, '') || '';
  const isHomepage = actualPath === '' || actualPath === '/';
  
  // Canonical URL logic:
  // - English homepage: https://lotterics.com/
  // - German homepage: https://lotterics.com/de
  // - English /predictions: https://lotterics.com/predictions
  // - German /predictions: https://lotterics.com/de/predictions
  let canonicalUrl;
  if (isHomepage) {
    canonicalUrl = currentLang === 'en' ? `${CANONICAL_BASE}/` : `${CANONICAL_BASE}/${currentLang}`;
  } else {
    canonicalUrl = currentLang === 'en' 
      ? `${CANONICAL_BASE}${actualPath}`
      : `${CANONICAL_BASE}/${currentLang}${actualPath}`;
  }
  
  // Generate hreflang links - each language points to its own canonical
  const hreflangLinks = SUPPORTED_LANGUAGES.map(lang => {
    let href;
    if (isHomepage) {
      href = lang === 'en' ? `${CANONICAL_BASE}/` : `${CANONICAL_BASE}/${lang}`;
    } else {
      href = lang === 'en' ? `${CANONICAL_BASE}${actualPath}` : `${CANONICAL_BASE}/${lang}${actualPath}`;
    }
    return {
      rel: 'alternate',
      hrefLang: lang,
      href: href
    };
  });
  
  // Add x-default pointing to English version (root for homepage, /path for inner)
  hreflangLinks.push({
    rel: 'alternate',
    hrefLang: 'x-default',
    href: isHomepage ? `${CANONICAL_BASE}/` : `${CANONICAL_BASE}${actualPath}`
  });
  
  // Generate breadcrumbs
  const breadcrumbs = generateBreadcrumbs(location.pathname, currentLang);
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);
  
  // Generate website schema (only on homepage)
  const websiteSchema = isHomepage ? generateWebsiteSchema(currentLang, localizedSiteDescription) : null;
  
  // WebPage schema for all pages
  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': type === 'article' ? 'Article' : 'WebPage',
    '@id': `${canonicalUrl}/#webpage`,
    'url': canonicalUrl,
    'name': fullTitle,
    'description': pageDescription,
    'inLanguage': currentLang,
    'isPartOf': { '@id': `${CANONICAL_BASE}/${currentLang}/#website` },
    ...(article && {
      'datePublished': article.datePublished,
      'dateModified': article.dateModified || article.datePublished,
      'author': {
        '@type': 'Organization',
        'name': SITE_NAME
      }
    }),
    ...(lottery && {
      'about': {
        '@type': 'Thing',
        'name': lottery.name,
        'description': `${lottery.name} lottery results, statistics, and predictions`
      }
    })
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={pageDescription} />
      
      {/* Robots directive */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Canonical URL - CRITICAL: Must be self-referencing and use fixed domain */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Language and Region */}
      <html lang={currentLang} />
      <meta httpEquiv="content-language" content={currentLang} />
      
      {/* hreflang tags for multilingual SEO - All 5 languages */}
      {hreflangLinks.map((link, idx) => (
        <link key={idx} rel={link.rel} hrefLang={link.hrefLang} href={link.href} />
      ))}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={`${CANONICAL_BASE}${image}`} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={{'en':'en_US','it':'it_IT','fr':'fr_FR','es':'es_ES','de':'de_DE'}[currentLang] || 'en_US'} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={`${CANONICAL_BASE}${image}`} />
      
      {/* Structured Data - BreadcrumbList (all pages) */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      
      {/* Structured Data - WebPage */}
      <script type="application/ld+json">
        {JSON.stringify(webPageSchema)}
      </script>
      
      {/* Structured Data - WebSite + Organization (homepage only) */}
      {websiteSchema && (
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
      )}
    </Helmet>
  );
};

/**
 * SEO configuration keys for specific pages (use with t() function)
 */
export const SEO_PAGES = {
  home: {
    titleKey: null, // Uses default from seo.home_title
    descriptionKey: 'seo.site_description'
  },
  lotteries: {
    titleKey: 'seo.lotteries_title',
    descriptionKey: 'seo.lotteries_desc'
  },
  predictions: {
    titleKey: 'seo.predictions_title',
    descriptionKey: 'seo.predictions_desc'
  },
  statistics: {
    titleKey: 'seo.statistics_title',
    descriptionKey: 'seo.statistics_desc'
  },
  about: {
    titleKey: 'seo.about_title',
    descriptionKey: 'seo.about_desc'
  },
  donate: {
    titleKey: 'seo.donate_title',
    descriptionKey: 'seo.donate_desc'
  },
  privacy: {
    titleKey: 'seo.privacy_title',
    descriptionKey: 'seo.privacy_desc'
  },
  terms: {
    titleKey: 'seo.terms_title',
    descriptionKey: 'seo.terms_desc'
  },
  disclaimer: {
    titleKey: 'seo.disclaimer_title',
    descriptionKey: 'seo.disclaimer_desc'
  }
};

/**
 * Get lottery-specific SEO config
 */
export function getLotterySEO(lotteryCode, lotteryName) {
  const name = lotteryName || lotteryCode.charAt(0).toUpperCase() + lotteryCode.slice(1);
  return {
    title: `${name} Results & Statistics`,
    description: `Latest ${name} results, jackpot amounts, hot/cold numbers, frequency analysis, and AI-powered predictions. Updated live after every draw.`
  };
}

export default SEOHead;
